import { createInstance, dateReviver } from '@/api';
import {
    CreateDeliveryTransformationDto,
    DeliveryEntityDetailDto,
    DeliveryTransformationClient,
    DeliveryTransformationDetailDto,
    OperationEntityDetailDto,
    UpdateDeliveryTransformationDto
} from '@/api/backoffice/clients';
import { i18n } from '@/i18n';
import { useAlertStore } from '@/modules/core/store/alert.store';
import { defineStore } from 'pinia';
import { useDeliveryProcessDefinitionEditStore } from '../delivery-process-definition/delivery-process-definition-edit.store';
import { copyDeliveryEntityValues } from '../../common';
import { copyOperationEntityValues } from '../../common/operation-entity.common';

export interface DeliveryTransformationStoreState {
    items?: { key: string; value: DeliveryTransformationDetailDto[] }[];
    loading: boolean;
    reloading: boolean;
}

export const client = new DeliveryTransformationClient('', createInstance(), dateReviver);

export const useDeliveryTransformationStore = defineStore('configuration-delivery-transformation-store', {
    state: (): DeliveryTransformationStoreState => ({
        items: [],
        loading: false,
        reloading: false
    }),
    getters: {
        byDefinition: (state) => {
            return (definitionId: number): DeliveryTransformationDetailDto[] => {
                const key = `definition-${definitionId}`;

                return state.items.find((x) => x.key === key)?.value ?? [];
            };
        }
    },
    actions: {
        updateOperationEntityUI(updatedOperationEntity: OperationEntityDetailDto) {
            this.items?.forEach((item) => {
                item.value?.forEach((entity) => {
                    copyOperationEntityValues(updatedOperationEntity, entity.fromEntity);
                    copyOperationEntityValues(updatedOperationEntity, entity.toEntity);
                });
            });
        },
        updateDeliveryEntityUI(updatedDeliveryEntity: DeliveryEntityDetailDto) {
            this.items?.forEach((item) => {
                item.value?.forEach((entity) => {
                    copyDeliveryEntityValues(updatedDeliveryEntity, entity.fromEntity);
                    copyDeliveryEntityValues(updatedDeliveryEntity, entity.toEntity);
                });
            });
        },
        async getByDefinition(definitionId: number, reloading: boolean = false) {
            reloading ? (this.reloading = true) : (this.loading = true);
            const key = `definition-${definitionId}`;

            try {
                const response = await client.getByDefinition(definitionId);
                const items = this.items.filter((x) => x.key !== key);
                if (response?.length) {
                    this.items = [...items, { key, value: response }];
                } else {
                    this.items = [...items];
                }
            } catch (error) {
                useAlertStore().handleException(
                    i18n.global.t('deliverytransformation.detail.error.get.title'),
                    error,
                    false
                );
            } finally {
                reloading ? (this.reloading = false) : (this.loading = false);
            }
        },
        async create(definitionId: number, dto: CreateDeliveryTransformationDto) {
            let hasError = false;
            this.reloading = true;
            try {
                const response = await client.create(dto);
                useAlertStore().setSuccess(i18n.global.t('deliverytransformation.detail.success.create.title'));
                this.getByDefinition(definitionId, true);
                return response.id;
            } catch (error) {
                hasError = true;
                useAlertStore().handleException(
                    i18n.global.t('deliverytransformation.detail.error.create.title'),
                    error
                );
            } finally {
                this.reloading = false;
                if(!hasError){
                    useDeliveryProcessDefinitionEditStore().closeCreate();
                }
            }
        },
        async update(definitionId: number, transformationId: number, dto: UpdateDeliveryTransformationDto) {
            let hasError = false;
            this.reloading = true;
            try {
                await client.update(transformationId, dto);
                useAlertStore().setSuccess(i18n.global.t('deliverytransformation.detail.success.update.title'));
                this.getByDefinition(definitionId, true);
            } catch (error) {
                hasError = true;
                useAlertStore().handleException(
                    i18n.global.t('deliverytransformation.detail.error.update.title'),
                    error
                );
            } finally {
                this.reloading = false;
                if(!hasError){
                    useDeliveryProcessDefinitionEditStore().closeEdit();
                }
            }
        },
        async remove(definitionId: number, id: number): Promise<boolean> {
            this.reloading = true;
            try {
                await client.delete(id);
                useAlertStore().setSuccess(i18n.global.t('deliverytransformation.detail.success.delete.title'));
                await this.getByDefinition(definitionId, true);
                return true;
            } catch (error) {
                useAlertStore().handleException(
                    i18n.global.t('deliverytransformation.detail.error.delete.title'),
                    error
                );
            } finally {
                this.reloading = false;
            }
            return false;
        },
        clear(definitionId: number) {
            const key = `definition-${definitionId}`;

            this.items = this.items.filter((x) => x.key !== key);
        }
    }
});
